export const notificationsSupported = () => 'Notification' in window;

export const notificationPermission = (): NotificationPermission | 'unsupported' =>
  notificationsSupported() ? Notification.permission : 'unsupported';

export async function requestNotifications(): Promise<NotificationPermission | 'unsupported'> {
  if (!notificationsSupported()) return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Shows a system notification while the page is hidden. Uses the service worker when present
 * (required on Android, where `new Notification()` throws), else the page-level constructor.
 */
const pageNotes = new Map<string, Notification>();

/**
 * Shows a system notification while the page is hidden. Uses the service worker when present
 * (required on Android, where `new Notification()` throws), else the page-level constructor.
 *
 * A same-tag notification *replaces* the old one without popping up again, so an unread
 * "Focus complete" would swallow "Break over". The previous one is closed first instead, which
 * keeps one entry per tag and still shows each alert. (`renotify` can't be used: it is invalid
 * together with `silent`, and the app plays its own chime.)
 */
export async function notify(title: string, body: string, tag = 'pomodoro-phase'): Promise<void> {
  if (!notificationsSupported() || Notification.permission !== 'granted' || !document.hidden) return;
  const options: NotificationOptions = {
    body,
    tag,
    icon: `${import.meta.env.BASE_URL}pwa-192x192.png`,
    badge: `${import.meta.env.BASE_URL}pwa-64x64.png`,
    silent: true,
  };
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      for (const n of await reg.getNotifications({ tag })) n.close();
      await reg.showNotification(title, options);
      return;
    }
  } catch {
    /* fall back below */
  }
  try {
    pageNotes.get(tag)?.close();
    pageNotes.set(tag, new Notification(title, options));
  } catch {
    /* unsupported in this context */
  }
}
